import React, { useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useLanguage } from '@/i18n/LanguageContext';

const LCMSLayout: React.FC = () => {
  const location = useLocation();
  const pathname = location.pathname;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { t } = useLanguage();

  const navItems = [
    {
      title: t('app.nav.dashboard'),
      href: '/lcms',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
          <path d="M2 10a8 8 0 018-8v8h8a8 8 0 11-16 0z" />
          <path d="M12 2.252A8.014 8.014 0 0117.748 8H12V2.252z" />
        </svg>
      ),
    },
    {
      title: t('app.nav.courseContent'),
      href: '/lcms/course-content',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
          <path d="M2 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1H3a1 1 0 01-1-1V4zM8 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1H9a1 1 0 01-1-1V4zM15 3a1 1 0 00-1 1v12a1 1 0 001 1h2a1 1 0 001-1V4a1 1 0 00-1-1h-2z" />
        </svg>
      ),
      submenu: [
        { title: t('course-content.allCourses'), href: '/lcms/course-content' },
        { title: t('course-content.createCourse'), href: '/lcms/course-content/new' },
      ],
    },
  ];

  const isActive = (path: string) => {
    if (path === '/lcms') {
      return pathname === '/lcms';
    }
    return pathname.startsWith(path);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col md:flex-row">
      {/* Mobile menu button */}
      <div className="md:hidden bg-white border-b p-4 flex justify-between items-center">
        <h1 className="text-xl font-semibold">{t('app.name')}</h1>
        <button
          onClick={toggleMobileMenu}
          className="p-2 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-100"
        >
          {isMobileMenuOpen ? (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          )}
        </button>
      </div>

      {/* Sidebar */}
      <div 
        className={`bg-white w-full md:w-64 shadow-md md:shadow-none flex-shrink-0 ${
          isMobileMenuOpen ? 'block' : 'hidden'
        } md:block`}
      >
        <div className="hidden md:block p-4 border-b">
          <h1 className="text-xl font-semibold">{t('app.name')}</h1>
        </div>
        <nav className="py-4">
          <ul>
            {navItems.map((item, index) => (
              <li key={index} className="mb-1">
                <Link 
                  to={item.href}
                  className={`flex items-center px-4 py-2 ${
                    isActive(item.href) 
                      ? 'bg-blue-50 text-blue-700 font-medium' 
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <span className="mr-3">{item.icon}</span>
                  {item.title}
                </Link>
                {item.submenu && (
                  <ul className="pl-10 mt-1">
                    {item.submenu.map((subItem, subIndex) => (
                      <li key={subIndex}>
                        <Link 
                          to={subItem.href}
                          className={`block py-2 px-4 text-sm ${
                            pathname === subItem.href
                              ? 'text-blue-700 font-medium' 
                              : 'text-gray-700 hover:text-blue-700'
                          }`}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          {subItem.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}

            {/* Content Section */}
            <li className="mt-6 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              {t('app.nav.contents')}
            </li>
            <li className="mt-2">
              <Link 
                to="/lcms/content"
                className={`flex items-center px-4 py-2 ${
                  pathname.startsWith('/lcms/content')
                    ? 'bg-blue-50 text-blue-700 font-medium' 
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span className="mr-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3z" />
                  </svg>
                </span>
                {t('app.nav.contents')}
              </Link>
            </li>
            <li>
              <Link 
                to="/lcms/metadata"
                className={`flex items-center px-4 py-2 ${
                  pathname.startsWith('/lcms/metadata')
                    ? 'bg-blue-50 text-blue-700 font-medium' 
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span className="mr-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 2a1 1 0 00-1 1v1H3a1 1 0 00-1 1v1h18V5a1 1 0 00-1-1h-7V3a1 1 0 00-1-1zM3 9a1 1 0 00-1 1v6a1 1 0 001 1h14a1 1 0 001-1v-6a1 1 0 00-1-1H3z" clipRule="evenodd" />
                  </svg>
                </span>
                Metadata
              </Link>
            </li>

            

            {/* Banks Section */}
            <li className="mt-6 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Banks
            </li>
            <li className="mt-2">
              <Link 
                to="/lcms/banks/questions"
                className={`flex items-center px-4 py-2 ${
                  pathname === '/lcms/banks/questions'
                    ? 'bg-blue-50 text-blue-700 font-medium' 
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span className="mr-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                  </svg>
                </span>
                Question Bank
              </Link>
            </li>
            <li>
              <Link 
                to="/lcms/banks/media"
                className={`flex items-center px-4 py-2 ${
                  pathname === '/lcms/banks/media'
                    ? 'bg-blue-50 text-blue-700 font-medium' 
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span className="mr-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                  </svg>
                </span>
                Media Bank
              </Link>
            </li>
          </ul>
        </nav>
      </div>

      {/* Content */}
      <div className="flex-grow p-6 md:px-8 overflow-auto">
        <Outlet />
      </div>
    </div>
  );
}

export default LCMSLayout; 